import {useMemo} from 'react';
import {paths} from 'packages/common/routes/paths';
import {Icon} from '@iconify/react';
import {usePermissions, useActiveCompany} from 'packages/common/hooks/session';
import {useGetServices} from 'packages/common/hooks/api/app/restaurants/Services';
import {useRestaurantMessagesUnreadSummary} from 'packages/common/hooks/api/app/restaurants/ReservationMessages';
import {isMessagingEnabled} from 'packages/common/constants/restaurants/reservationMessages';
import Label from 'packages/common/components/label';
import {SETTINGS_FEATURES} from 'apps/dashboard/pages/app/settings/config/settingsSections';

/**
 * Génère la structure de navigation du menu latéral
 *
 * Le menu est construit dynamiquement en fonction de :
 * 1. L'abonnement de l'entreprise aux modules (hasModuleAccess)
 * 2. Les permissions de l'utilisateur sur les features (hasFeatureAccess)
 *
 * CompanyAdmin a accès à tout (permissions implicites).
 * CompanyUser n'a accès qu'aux features configurées par l'entreprise.
 *
 * @return {Array} Structure du menu pour le composant de navigation
 */
export function useNavData() {
  const {hasModuleAccess, hasFeatureAccess, hasAnyFeatureAccess} = usePermissions();
  const {companyId} = useActiveCompany();

  // Récupère la config restaurant uniquement si le module est accessible — sinon
  // companyId=null désactive le fetch dans useGetServices. Sert à savoir s'il faut
  // afficher l'entrée de menu dédiée « Liste d'attente ».
  const restaurantAccessible = hasModuleAccess('restaurant');
  const {config: restaurantConfig} = useGetServices(restaurantAccessible ? companyId : null);
  const waitlistEnabled = restaurantConfig?.waitlistSettings?.enabled === true;
  const waitlistDisplayMode = restaurantConfig?.waitlistSettings?.displayMode || 'inline';
  const showWaitlistMenu = waitlistEnabled && waitlistDisplayMode !== 'inline';

  // Messagerie : l'entrée n'existe que si l'établissement l'a ouverte, et porte
  // le nombre de discussions qui attendent une lecture.
  const showMessagesMenu = isMessagingEnabled(restaurantConfig) && hasFeatureAccess('restaurant.reservations');
  const {unreadConversations} = useRestaurantMessagesUnreadSummary(showMessagesMenu ? companyId : null);

  const data = useMemo(() => {
    const baseMenu = [];

    // ═══════════════════════════════════════════════════════════════════════════
    // SECTION PRINCIPALE
    // ═══════════════════════════════════════════════════════════════════════════

    const mainItems = [
      {
        title: 'Dashboard',
        path: paths.app.dashboard,
        icon: <Icon icon="solar:widget-5-linear" height="24" />,
      },
    ];

    // Clients
    if (hasFeatureAccess('customers')) {
      mainItems.push({
        title: 'Clients',
        path: paths.app.customers.list,
        icon: <Icon icon="ph:users-three" height="24" />,
      });
    }

    // Newsletters : transverse aux modules métier, elle s'adresse aux contacts
    // de l'établissement quel que soit son activité. Le module encadre le
    // déploiement progressif — il ne correspond à aucun abonnement, mais à la
    // liste d'établissements ouverte par le super admin. Sans lui, tout
    // propriétaire verrait l'entrée dès la première mise en production.
    if (hasModuleAccess('newsletter') && hasFeatureAccess('newsletter.campaigns')) {
      mainItems.push({
        title: 'Newsletters',
        path: paths.app.newsletter.list,
        icon: <Icon icon="solar:letter-unread-linear" height="24" />,
      });
    }

    baseMenu.push({items: mainItems});

    // ═══════════════════════════════════════════════════════════════════════════
    // SECTION BONS CADEAUX
    // ═══════════════════════════════════════════════════════════════════════════

    // Requiert l'activation du module bons cadeaux ET les permissions sur les features
    if (hasModuleAccess('giftvouchers')) {
      const giftVoucherItems = [];

      if (hasFeatureAccess('giftVoucher.catalog')) {
        giftVoucherItems.push({
          title: 'Catalogue',
          path: paths.app.giftVoucher.catalog.list,
          icon: <Icon icon="solar:clipboard-list-linear" height="24" />,
        });
      }

      if (hasFeatureAccess('giftVoucher.manage')) {
        giftVoucherItems.push({
          title: 'Tous mes bons',
          path: paths.app.giftVoucher.manage,
          icon: <Icon icon="solar:gift-outline" height="24" />,
        });
      }

      if (hasFeatureAccess('giftVoucher.promotions')) {
        giftVoucherItems.push({
          title: 'Promotions',
          path: paths.app.giftVoucher.promotions.list,
          icon: <Icon icon="solar:tag-price-linear" height="24" />,
        });
      }

      if (hasFeatureAccess('giftVoucher.statistics')) {
        giftVoucherItems.push({
          title: 'Statistiques',
          path: paths.app.giftVoucher.statistics,
          icon: <Icon icon="solar:chart-2-outline" height="24" />,
        });
      }

      // Afficher la section seulement s'il y a des items accessibles
      if (giftVoucherItems.length > 0) {
        baseMenu.push({
          subheader: 'Bons cadeaux',
          items: giftVoucherItems,
        });
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SECTION WELLNESS
    // Requiert l'abonnement au module ET les permissions sur les features
    // ═══════════════════════════════════════════════════════════════════════════

    if (hasModuleAccess('wellness')) {
      const wellnessItems = [];

      if (hasFeatureAccess('wellness.calendar')) {
        wellnessItems.push({
          title: 'Calendrier',
          path: paths.app.wellness.calendar.root,
          icon: <Icon icon="carbon:calendar" height="24" />,
        });
      }

      if (hasFeatureAccess('wellness.bookings')) {
        wellnessItems.push({
          title: 'Réservations',
          path: paths.app.wellness.bookings.root,
          icon: <Icon icon="solar:calendar-mark-linear" height="24" />,
        });
      }

      if (hasFeatureAccess('wellness.pos')) {
        wellnessItems.push({
          title: 'Caisse',
          path: paths.app.wellness.pos.sale,
          icon: <Icon icon="solar:hand-money-outline" height="24" />,
        });
      }

      if (wellnessItems.length > 0) {
        baseMenu.push({
          subheader: 'Wellness',
          items: wellnessItems,
        });
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SECTION RESTAURANT
    // Requiert l'abonnement au module ET les permissions sur les features
    // ═══════════════════════════════════════════════════════════════════════════

    if (hasModuleAccess('restaurant')) {
      const restaurantItems = [];

      if (hasFeatureAccess('restaurant.reservations')) {
        restaurantItems.push({
          title: 'Réservations',
          path: paths.app.restaurant.reservations,
          icon: <Icon icon="solar:calendar-mark-linear" height="24" />,
        });

        // Liste d'attente : visible uniquement si la feature est activée ET que
        // l'utilisateur a choisi un affichage 'separate' ou 'both' dans les settings.
        if (showWaitlistMenu) {
          restaurantItems.push({
            title: 'Liste d\'attente',
            path: paths.app.restaurant.waitlist,
            icon: <Icon icon="solar:clock-circle-outline" height="24" />,
          });
        }

        if (showMessagesMenu) {
          restaurantItems.push({
            title: 'Messages',
            path: paths.app.restaurant.messages,
            icon: <Icon icon="mdi:message-text-outline" height="24" />,
            info: unreadConversations > 0 ? <Label color="primary" variant="filled">{unreadConversations}</Label> : null,
          });
        }
      }

      if (hasFeatureAccess('restaurant.reviews')) {
        restaurantItems.push({
          title: 'Avis clients',
          path: paths.app.restaurant.reviews,
          icon: <Icon icon="solar:star-outline" height="24" />,
        });
      }

      if (hasFeatureAccess('restaurant.statistics')) {
        restaurantItems.push({
          title: 'Statistiques',
          path: paths.app.restaurant.statistics,
          icon: <Icon icon="solar:chart-2-outline" height="24" />,
        });
      }

      if (restaurantItems.length > 0) {
        baseMenu.push({
          subheader: 'Restaurant',
          items: restaurantItems,
        });
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SECTION ADMINISTRATION
    // Visible si l'utilisateur a au moins une permission settings
    // ═══════════════════════════════════════════════════════════════════════════

    if (hasAnyFeatureAccess(SETTINGS_FEATURES)) {
      baseMenu.push({
        subheader: 'Administration',
        items: [
          {
            title: 'Paramètres',
            path: paths.app.settings.root,
            icon: <Icon icon="solar:settings-linear" height="24" />,
          },
        ],
      });
    }

    return baseMenu;
  }, [hasModuleAccess, hasFeatureAccess, hasAnyFeatureAccess, showWaitlistMenu, showMessagesMenu, unreadConversations]);

  return data;
}
